import { useTaskTranslation } from './useTaskTranslation';
import { designationByRoleId } from '../../../constants/designations';
import { useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../store/store';
import {
  getPersonsInLocation,
  selectLocationId,
  selectPersonsInLocation,
  IPerson,
} from '../../../store/slices/auth';
import type { TaskMerchantPersonData } from '../../../store/slices/tasks';

export const hasValidAssigneeIds = (person: IPerson) =>
  [person.person_id, person.person_external_id].every(
    (value) => value != null && String(value).trim() !== '' && Number.isFinite(Number(value))
  );

export const useTaskAssigneeSelection = () => {
  const { translations } = useTaskTranslation();
  const dispatch = useAppDispatch();
  const locationId = useAppSelector(selectLocationId);
  const locationPersons = useAppSelector(selectPersonsInLocation);
  const persons: IPerson[] = useMemo(() => locationPersons ?? [], [locationPersons]);
  const [loadedLocationId, setLoadedLocationId] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState('');
  const [retry, setRetry] = useState(0);
  const [search, setSearch] = useState('');
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  useEffect(() => {
    setSelectedKey(null);
    setSearch('');
  }, [locationId]);

  useEffect(() => {
    let active = true;
    setError('');
    setSelectedKey(null);
    setLoadedLocationId(null);
    if (!locationId) return;
    if (persons.length > 0) {
      setLoadedLocationId(locationId);
      setPending(false);
      return;
    }
    setPending(true);
    dispatch(getPersonsInLocation({ locationId }))
      .unwrap()
      .then(() => {
        if (!active) return;
        setLoadedLocationId(locationId);
      })
      .catch(() => {
        if (active) setError('task_board.unable_to_load_assignees_please_retry');
      })
      .finally(() => {
        if (active) setPending(false);
      });
    return () => {
      active = false;
    };
  }, [dispatch, locationId, retry, persons.length]);

  const ready = Boolean(locationId) && loadedLocationId === locationId && !pending && !error;
  const options = useMemo(
    () =>
      persons.map((person, index) => ({ person, key: `${person.person_id ?? 'person'}-${index}` })),
    [persons]
  );
  const selectedPerson = options.find(({ key }) => key === selectedKey)?.person;
  const filteredOptions = useMemo(() => {
    const query = search.trim().toLowerCase();
    return options.filter(
      ({ person }) =>
        !query ||
        [
          `${person.person_firstname || ''} ${person.person_lastname || ''}`,
          person.person_id,
          person.person_external_id,
          designationByRoleId(person.role_id),
        ].some((value) => value != null && String(value).toLowerCase().includes(query))
    );
  }, [options, search]);
  console.log("selectedPerson", selectedPerson)

  const getAssigneePayload = (): TaskMerchantPersonData => {
    if (!ready)
      throw new Error(
        (error ? translations(error) : '') ||
        translations('task_board.wait_for_the_assignee_list_to_load')
      );
    if (!selectedPerson)
      throw new Error(translations('task_board.select_a_person_to_assign_the_task_to'));
    if (!hasValidAssigneeIds(selectedPerson))
      throw new Error(translations('task_board.select_a_person_with_valid_assignee_ids'));
    return {
      id: Number(selectedPerson.person_id),
      externalId: Number(selectedPerson.person_external_id),
      // The location endpoint does not expose roles. Never copy the current
      // user's role onto a different person.
      roles: [],
      name: [selectedPerson.person_firstname, selectedPerson.person_lastname]
        .filter(Boolean)
        .join(' '),
      givenName: selectedPerson.person_firstname || '',
    };
  };

  return {
    search,
    setSearch,
    selectedKey,
    selectedPerson,
    selectPerson: setSelectedKey,
    clearSelection: () => setSelectedKey(null),
    hasSelection: selectedKey != null,
    options,
    filteredOptions,
    hasPersons: persons.length > 0,
    pending: !locationId || pending || (!ready && !error),
    ready,
    error: error ? translations(error) : '',
    retryPersons: () => setRetry((value) => value + 1),
    getAssigneePayload,
  };
};

export type TaskAssigneeSelection = ReturnType<typeof useTaskAssigneeSelection>;
